<template>
  <div>
    <b-alert :show="!!error" variant="danger">{{ error }}</b-alert>

    <b-card class="licenses animated fadeIn">
      <div class="licenses__header text-center mb-5">
        <h1 class="licenses__header-text">Licenses</h1>
        <b-btn to="/quote" variant="primary" class="my-1">
          Request a Quote
        </b-btn>
      </div>

      <section v-if="licensesUsing.length">
        <h3>Currently Using</h3>
        <img
          v-if="licensesUsing[0] && productDetails[licensesUsing[0].type]"
          :src="productDetails[licensesUsing[0].type].img"
          class="licenses__product-logo"
          alt="Product logo"
        />
      </section>

      <section class="licenses-owned">
        <template v-if="!licensesOwnedFiltered.length">
          <h3>Owned</h3>
          <p>
            You do not currently own a license. Why not
            <b-link to="/licenses/add">add one</b-link>?
          </p>
        </template>

        <LicensesManage
          v-else
          :licenses="licensesOwnedFiltered"
          title="Owned"
          add-license
        />
      </section>

      <section v-if="canManageOrgLicenses">
        <LicensesManage
          :licenses="org.licenses"
          :assign-emails="orgMemberEmails"
          title="Org Licenses"
        />
      </section>

      <section v-if="licensesUsing.length" class="licenses-assigned">
        <h3>Assigned</h3>
        <b-table
          :items="licensesUsing"
          :fields="assignedFieldsComputed"
          :current-page="assignedCurrentPage"
          :per-page="perPage"
          sort-by="type"
          responsive="lg"
          striped
        >
          <template #cell(type)="{ item }">
            <i :class="productDetails[item.type].icon"></i>
            {{ item.type === "edu" ? "Academic" : item.type | capitalize }}
            <i
              v-if="!isEmptyConfig(item.data) && isAdmin"
              class="icon-flag text-danger"
            />

            <i
              v-if="isPluginEnabled(item.data)"
              v-b-tooltip.hover="'Stacks Enabled'"
              class="icon-layers text-indigo"
            />

            <b-badge
              class="ml-2"
              variant="secondary"
              v-if="isViewerLicense(item.data)"
              >collab</b-badge
            >
          </template>

          <template #cell(owner)="{ item }">
            {{ item.owner ? item.owner.name : item.org.name }}
          </template>

          <template #cell(email)="{ item }">
            {{ item.owner ? item.owner.email : "-" }}
          </template>

          <template #cell(expires)="{ item }">
            {{ item.expired_time | date }}
          </template>

          <template #cell(download)="{ item, index }">
            <b-button
              v-if="item.type === 'enterprise'"
              variant="link"
              @click="downloadLicense(item, $event.target)"
              :aria-label="`Download license ${index + 1}`"
              class="licenses-assigned__download"
            >
              Download
              <i class="icon-cloud-download ml-2"></i>
            </b-button>
            <template v-else>Commercial Only</template>
          </template>
        </b-table>
      </section>
    </b-card>
  </div>
</template>

<script>
import { mapGetters, mapState } from "vuex";

import API from "@/api";
import productDetails from "@/data/products";
import { downloadText } from "@/helpers/download";

import LicensesManage from "@/components/licenses/LicensesManage";

const errMessages = {
  LOGIN_INVALID: "Please use a valid email"
};

export default {
  components: {
    LicensesManage
  },

  metaInfo: {
    titleTemplate: "Licenses - %s"
  },

  data: () => ({
    productDetails,

    assignedFields: [
      { key: "type", sortable: true },
      { key: "owner", sortable: true, class: "text-center" },
      { key: "email", sortable: true, class: "text-center" },
      { key: "expires", sortable: true, class: "text-center" },
      { key: "download", label: "Offline License", class: "text-center" }
    ],
    assignedCurrentPage: 1,
    org: {
      licenses: [],
      members: []
    },
    perPage: 50,
    error: null
  }),

  computed: {
    ...mapGetters(["isAdmin", "hasPermission"]),
    ...mapState(["user"]),
    ...mapState({
      licensesOwned: state => state.licenses.owned,
      licensesUsing: state => state.licenses.using
    }),

    assignedFieldsComputed() {
      // hide download unless admin
      return this.assignedFields.slice(0, this.isAdmin ? undefined : -1);
    },

    canManageOrgLicenses() {
      if (!this.org.id) return false;
      const hasLicenses = this.org.licenses && this.org.licenses.length;
      const me = this.org.members.find(m => m.id === this.user.id);
      const isOrgAdmin = me && ["admin", "owner"].includes(me.role);
      const hasPermission = this.hasPermission("license.assign");
      return hasLicenses && (isOrgAdmin || hasPermission);
    },

    licensesOwnedFiltered() {
      const hasEnterprise =
        this.licensesOwned.some(l => l.type === "enterprise") ||
        this.licensesUsing.some(l => l.type === "enterprise");
      if (!hasEnterprise) return this.licensesOwned;

      return this.licensesOwned.filter(l => {
        return l.type !== "trial" || new Date(l.expired_time) > Date.now();
      });
    },

    orgMemberEmails() {
      return this.org.members.map(m => m.email);
    }
  },

  watch: {
    "user.organization.id": {
      async handler(id) {
        if (!id) return;
        const { success, results } = await API.getOrgDetails(id);
        if (!success) return;
        this.org = results;
      },
      immediate: true
    }
  },

  async mounted() {
    await Promise.all([
      this.$store.dispatch("licenses/using"),
      this.$store.dispatch("licenses/owned")
    ]);
  },

  methods: {
    async downloadLicense(license, clickedEl) {
      try {
        const { success, results } = await API.downloadLicense(license.id);
        if (success) {
          const name = /user\.name: (.+)/.exec(results.content)[1];
          downloadText(
            `nanome-license-${name}-${license.id}.txt`,
            results.content
          );
          clickedEl.focus();
        }
      } catch (err) {
        console.warn(err);
        this.error =
          errMessages[err.error.code] ||
          "There was an error downloading the license.";
      }
    },

    isEmptyConfig({ config, extras } = {}) {
      if (!config && !extras) return true;

      const merged = {
        ...config,
        ...extras
      };
      if (!Object.keys(merged).length) return true;

      return false;
    },

    isPluginEnabled(data = {}) {
      if (this.isEmptyConfig(data)) return false;
      return data.extras && data.extras["allow-plugins"];
    },

    isViewerLicense(data = {}) {
      if (this.isEmptyConfig(data)) return false;
      return data.extras && data.extras["viewer-license"];
    }
  }
};
</script>

<style lang="scss">
.licenses {
  &__header {
    align-items: center;

    @include breakpoint(tablet) {
      display: flex;
      text-align: left !important;
    }

    &-text {
      flex-grow: 1;
    }
  }

  section {
    margin-top: 50px;
  }

  &__product-logo {
    max-width: 50em;
    width: 100%;
  }
}

.licenses-assigned {
  .btn-link:hover {
    text-decoration: none;
  }

  table {
    white-space: nowrap;
  }

  table td {
    vertical-align: middle;
  }

  table button {
    vertical-align: unset;
    padding: 0;
  }

  &__download {
    display: flex;
    align-items: center;
    margin: 0 auto;
  }
}
</style>
